import { useCallback, useEffect } from 'react';
import { Nav } from '../sections/Nav';
import { Hero } from '../sections/Hero';
import { Pitch } from '../sections/Pitch';
import { How } from '../sections/How';
import { Workflow } from '../sections/Workflow';
import { Persona } from '../sections/Persona';
import { Output } from '../sections/Output';
import { Pricing } from '../sections/Pricing';
import { FAQ } from '../sections/FAQ';
import { FinalCTA } from '../sections/FinalCTA';
import { Footer } from '../sections/Footer';
import { CALENDLY_URL } from '../lib/links';

export default function Landing() {
  const onBook = useCallback(() => {
    window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {
    // Reveal-on-scroll. Each <section> below the hero starts hidden (via the
    // .js-ready selector in app.css) and animates in when it crosses ~12% of
    // the viewport. We unobserve once revealed so the transition is one-way
    // and never re-fires on scroll-up.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const sections = document.querySelectorAll<HTMLElement>('section:not(.hero)');

    if (reduce || !('IntersectionObserver' in window)) {
      sections.forEach((el) => el.classList.add('is-revealed'));
    } else {
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              io.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.12, rootMargin: '0px 0px -6% 0px' },
      );
      sections.forEach((el) => io.observe(el));
      return () => io.disconnect();
    }
  }, []);

  useEffect(() => {
    // Nav state — toggles a denser background once the user has scrolled past
    // the hero. Plays well with the backdrop-blur for a glassy "lifted" feel.
    const onScroll = () => {
      const nav = document.querySelector('.nav');
      if (!nav) return;
      if (window.scrollY > 24) nav.classList.add('is-scrolled');
      else nav.classList.remove('is-scrolled');
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="site surface" data-theme="light">
      <Nav onBook={onBook} />
      <Hero onBook={onBook} />
      <hr className="section-rule" />
      <Pitch />
      <hr className="section-rule" />
      <How />
      <hr className="section-rule" />
      <Workflow />
      <hr className="section-rule" />
      <Persona />
      <hr className="section-rule" />
      <Output />
      <hr className="section-rule" />
      <Pricing onBook={onBook} />
      <hr className="section-rule" />
      <FAQ />
      <FinalCTA onBook={onBook} />
      <Footer />
    </div>
  );
}
